import { useState, useEffect, useRef, useCallback } from 'react';
import { useLoader } from '../../../../context/LoaderContext';
import { DISPATCH_COLUMN, POPUP_TYPE, GROUP_BY } from '../../../../constants';
import { useDebounce } from '../../../../hooks';
import { vendorDispatch, getGroupBy } from '../../../../service';
import { popup } from '../../../../utils';

/**
 * useDispatchData - Hook for managing API fetching, data state, and effect orchestration
 *
 * This is the central data fetching hook for the dispatch module. It:
 * - Fetches dispatch records from the API with pagination
 * - Fetches groupBy available values
 * - Orchestrates effects for search, filter, pagination, and groupBy changes
 * - Updates URL parameters for shareable links
 * - Populates sorting arrays and selectable record counts
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.filters - Filter state from useDispatchFilters
 * @param {Object} options.groupByState - GroupBy state from useDispatchGroupBy
 * @param {Object} options.pagination - Pagination state from useDispatchPagination
 * @param {Object} options.urlState - URL state from useDispatchUrlState
 *
 * @returns {Object} Data state, fetch functions, and session state
 */
// Requests follow explicit submit/filter/page/group events, not callback identity changes.
const consumeDispatchTrigger = (ref, key, next) => {
  const previous = ref.current[key];
  if (previous && next.every((value, i) => Object.is(value, previous[i]))) return false;
  ref.current[key] = next;
  return true;
};

export const useDispatchData = ({
  filters,
  groupByState,
  pagination,
  urlState,
}) => {
  const triggers = useRef({});
  // Permit a fresh setup when React StrictMode replays mount effects.
  useEffect(() => () => { triggers.current = {}; }, []);
  const { setLoading } = useLoader();

  // =====================================================================
  // DATA STATE
  // =====================================================================

  /** Array of dispatch records for the current page */
  const [dispatchData, setDispatchData] = useState([]);

  /** Loading state for showing spinners during API calls */
  const [isLoading, setIsLoading] = useState(false);

  /** Flag to trigger a data refresh (set to true, effect will fetch and reset to false) */
  const [reload, setReload] = useState(false);

  /** Flag indicating whether the initial data fetch has completed */
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // =====================================================================
  // DESTRUCTURE DEPENDENCIES
  // =====================================================================

  const {
    searchTerm,
    search,
    setSearch,
    dispatchStatusFilter,
    stateFilter,
    billStatusFilter,
    buildSearchPayload,
    buildFilterPayload,
    setDispatchStatusOptions,
    setStateOptions,
    setBillStatusOptions,
  } = filters;

  const {
    groupBy,
    groupArray,
    setGroupArray,
    groupApplied,
    setGroupApplied,
    selectedItem,
    availableValuesGroupBy,
    setAvailableValuesGroupBy,
    shouldApplyGroupBy,
    buildGroupByPayload,
    loadCachedValues,
  } = groupByState;

  const {
    pageNumber,
    setPageNumber,
    setPaginationData,
    setSelectableRecords,
    setCreateBillSelectableRecords,
    setPrintPackingSelectableRecords,
  } = pagination;

  const { updateUrlParams, searchParams } = urlState;

  // =====================================================================
  // API FETCHING FUNCTIONS
  // =====================================================================

  /**
   * Fetches dispatch list data from the API
   * Debounced at 400ms to prevent excessive API calls during rapid filter changes
   *
   * @param {Object} params - API request parameters
   */
  const fetchDispatchList = useDebounce(async (params = {}) => {
    setIsLoading(true);
    try {
      const response = await vendorDispatch({ data: params });
      setLoading(false);

      if (response?.data?.records?.length > 0) {
        setReload(false);
        setDispatchData(response.data.records);
        setPaginationData(response.data.params);
        setGroupArray(response?.data?.groupby || []);

        // Set selectable record counts for different modes
        setSelectableRecords(response?.data?.selectable_records || 0);
        setCreateBillSelectableRecords(
          response?.data?.create_bill_selectable_records || 0,
        );
        setPrintPackingSelectableRecords(
          response?.data?.group_print_packing_count || 0,
        );

        // Set filter dropdown options from API response
        const dispatchStatusField = DISPATCH_COLUMN[16].dataField[0];
        const stateField = DISPATCH_COLUMN[17].dataField[0];
        const billStatusField = DISPATCH_COLUMN[15].dataField[0];

        if (
          response?.data[dispatchStatusField] &&
          Array.isArray(response?.data[dispatchStatusField])
        ) {
          setDispatchStatusOptions(response?.data[dispatchStatusField]);
        }

        if (
          response?.data[stateField] &&
          Array.isArray(response?.data[stateField])
        ) {
          setStateOptions(response?.data[stateField]);
        }

        if (
          response?.data[billStatusField] &&
          Array.isArray(response?.data[billStatusField])
        ) {
          setBillStatusOptions(response?.data[billStatusField]);
        }
      } else {
        // No records found - clear the data
        setDispatchData([]);
      }
    } catch (error) {
      setLoading(false);
      popup(POPUP_TYPE.ERR, error.message);
    } finally {
      setIsLoading(false);
    }
  }, 400);

  /**
   * Fetches available values for a groupBy field
   * Used to populate the InputWithSearch dropdown for filtering by specific groups
   *
   * @param {Object} params - API request parameters
   */
  const fetchGroupByValues = useDebounce(async (params = {}) => {
    try {
      const response = await getGroupBy({ data: params });
      setLoading(false);

      const fieldName = params.field_name;
      if (response?.data && response?.data[fieldName]) {
        setAvailableValuesGroupBy(response?.data[fieldName]);
      }
    } catch (error) {
      setLoading(false);
      popup(POPUP_TYPE.ERR, error.message);

      // API failed - try to use cached values as fallback
      const cachedValues = loadCachedValues(params.field_name);
      if (cachedValues) {
        setAvailableValuesGroupBy(cachedValues);
      }
    }
  }, 400);

  // =====================================================================
  // HELPER FUNCTIONS
  // =====================================================================

  /**
   * Refreshes the available values for the current groupBy field
   * Called when search/sort filters change to get updated group options
   *
   * @param {boolean} includeFilters - Whether to include current search/sort in the request
   */
  const refreshGroupByOptions = useCallback((includeFilters = true) => {
    if (initialLoadComplete && groupArray.includes(groupBy)) {
      let params = {
        related_to: GROUP_BY.DISPATCH,
        field_name: groupBy,
      };

      if (includeFilters) {
        const searchPayload = buildSearchPayload();
        if (Object.keys(searchPayload).length > 0) {
          params.search = searchPayload;
        }

        const filterPayload = buildFilterPayload();
        if (
          Object.keys(filterPayload.selection || {}).length > 0 ||
          filterPayload.dispatch_bill_id
        ) {
          params.sort = filterPayload;
        }
      }

      fetchGroupByValues(params);
    }
  }, [buildFilterPayload, buildSearchPayload, fetchGroupByValues, groupArray, groupBy, initialLoadComplete]);

  // =====================================================================
  // MAIN DATA FETCHING FUNCTION
  // =====================================================================

  /**
   * Fetches dispatch list with current search, sort, pagination, and groupBy settings
   *
   * This is the main function called by effects and action handlers.
   * It builds the complete API payload and updates URL params.
   *
   * @param {Object} additionalParams - Override or add to default parameters
   */
  const getDispatchList = useCallback((additionalParams = {}) => {
    setIsLoading(true);
    const shouldUseGroupBy = shouldApplyGroupBy();

    // Build base request parameters
    const params = {
      page_number: additionalParams.page_number || pageNumber,
      search: buildSearchPayload(),
      sort: buildFilterPayload(),
      groupby_field: shouldUseGroupBy ? groupBy : '',
      ...buildGroupByPayload(),
      ...additionalParams,
    };

    // Sync URL params with current state for shareable links
    updateUrlParams({
      search: searchTerm,
      dispatchStatus:
        dispatchStatusFilter !== DISPATCH_COLUMN[16].name
          ? dispatchStatusFilter
          : '',
      state: stateFilter !== DISPATCH_COLUMN[17].name ? stateFilter : '',
      billStatus:
        billStatusFilter !== DISPATCH_COLUMN[15].name ? billStatusFilter : '',
      page: params.page_number,
      groupBy: shouldUseGroupBy ? groupBy : '',
      groupItems: shouldUseGroupBy && selectedItem.length ? selectedItem : [],
      groupApplied: shouldUseGroupBy,
    });

    fetchDispatchList(params);
  }, [billStatusFilter, buildFilterPayload, buildGroupByPayload, buildSearchPayload, dispatchStatusFilter, fetchDispatchList, groupBy, pageNumber, searchTerm, selectedItem, shouldApplyGroupBy, stateFilter, updateUrlParams]);

  // =====================================================================
  // EFFECTS
  // =====================================================================

  /**
   * Effect: Initial load - restore state from URL and fetch data
   */
  useEffect(() => {
    if (!consumeDispatchTrigger(triggers, 'restoreInitialQuery', [])) return;
    // Restore selected items from URL if needed
    const urlGroupItems = searchParams.get('groupItems');
    const urlGroupBy = searchParams.get('groupBy');
    
    if (urlGroupBy && urlGroupBy !== GROUP_BY.TITLE && urlGroupItems) {
      // We have groupBy with items in URL - need to restore before fetching
      const idList = urlGroupItems.split('|');
      
      // Fetch group options first
      fetchGroupByValues({
        related_to: GROUP_BY.DISPATCH,
        field_name: urlGroupBy,
        search: buildSearchPayload(),
        sort: buildFilterPayload(),
      });
      
      // Set groupBy and groupApplied immediately
      if (urlGroupBy !== groupBy) {
        groupByState.setGroupBy(urlGroupBy);
      }
      setGroupApplied(true);
      
      // Restore selectedItem from URL - just set IDs for now
      // They will be converted to full items when availableValuesGroupBy loads
      groupByState.setSelectedItem(idList);
    } else if (urlGroupBy && urlGroupBy !== GROUP_BY.TITLE) {
      // GroupBy without items - just fetch group options
      fetchGroupByValues({
        related_to: GROUP_BY.DISPATCH,
        field_name: urlGroupBy,
        search: buildSearchPayload(),
        sort: buildFilterPayload(),
      });
    }

    // Always fetch data on initial load
    getDispatchList();
    setInitialLoadComplete(true);
  }, [buildFilterPayload, buildSearchPayload, fetchGroupByValues, getDispatchList, groupBy, groupByState, searchParams, setGroupApplied]);

  /**
   * Effect: Convert selectedItem IDs to full items when availableValuesGroupBy loads
   */
  useEffect(() => {
    if (!consumeDispatchTrigger(triggers, 'availableValuesGroupBy', [availableValuesGroupBy])) return;
    const urlGroupItems = searchParams.get('groupItems');
    if (urlGroupItems && availableValuesGroupBy.length > 0 && selectedItem.length > 0) {
      // Check if selectedItem contains only IDs (strings/numbers) vs full items (arrays)
      const hasOnlyIds = selectedItem.every(item => !Array.isArray(item));
      
      if (hasOnlyIds) {
        // Convert IDs to full items
        const fullItems = selectedItem.map((id) => {
          const match = availableValuesGroupBy.find(
            (item) => Array.isArray(item) && item[0].toString() === id.toString(),
          );
          return match || id;
        });
        groupByState.setSelectedItem(fullItems);
      }
    }
  }, [availableValuesGroupBy, groupByState, searchParams, selectedItem]); // Trigger when availableValuesGroupBy loads

  /**
   * Effect: Handle selected group items change
   */
  useEffect(() => {
    if (!consumeDispatchTrigger(triggers, 'selectedItem', [selectedItem])) return;
    if (initialLoadComplete && selectedItem.length > 0) {
      setGroupApplied(true);

      // Reset to page 1 only when first applying groupBy
      const prevItems = searchParams.get('groupItems');
      const wasEmpty = !prevItems || prevItems.split('|').length === 0;

      if (wasEmpty) {
        setPageNumber(1);
        getDispatchList({ page_number: 1 });
      } else {
        getDispatchList({ page_number: pageNumber });
      }
    }
  }, [selectedItem, getDispatchList, initialLoadComplete, pageNumber, searchParams, setGroupApplied, setPageNumber]); // Only trigger on selectedItem changes

  /**
   * Effect: Handle search execution
   */
  useEffect(() => {
    if (!consumeDispatchTrigger(triggers, 'search', [search])) return;
    if (search && initialLoadComplete) {
      setPageNumber(1);
      getDispatchList({ page_number: 1 });
      setSearch(false);

      // Refresh group options with search filter applied
      if (groupArray.includes(groupBy)) {
        refreshGroupByOptions(true);
      }
    }
  }, [search, getDispatchList, groupArray, groupBy, initialLoadComplete, refreshGroupByOptions, setPageNumber, setSearch]); // Only trigger on search flag change

  /**
   * Effect: Handle page number changes
   */
  useEffect(() => {
    if (!consumeDispatchTrigger(triggers, 'pageNumber', [pageNumber])) return;
    const currentUrlPage = searchParams.get('page');
    if (
      initialLoadComplete &&
      currentUrlPage !== pageNumber.toString() &&
      pageNumber !== 0
    ) {
      getDispatchList();
    }
  }, [pageNumber, getDispatchList, initialLoadComplete, searchParams]); // Only trigger on page number change

  /**
   * Effect: Handle filter dropdown changes
   */
  useEffect(() => {
    if (!consumeDispatchTrigger(triggers, 'dispatchStatusFilter_stateFilter_billStatusFilter', [dispatchStatusFilter, stateFilter, billStatusFilter])) return;
    if (initialLoadComplete) {
      const initialDispatchStatus =
        searchParams.get('dispatchStatus') || DISPATCH_COLUMN[16].name;
      const initialState =
        searchParams.get('state') || DISPATCH_COLUMN[17].name;
      const initialBillStatus =
        searchParams.get('billStatus') || DISPATCH_COLUMN[15].name;

      if (
        dispatchStatusFilter !== initialDispatchStatus ||
        stateFilter !== initialState ||
        billStatusFilter !== initialBillStatus
      ) {
        setPageNumber(1);
        getDispatchList({ page_number: 1 });

        // Refresh group options
        if (groupArray.includes(groupBy)) {
          const includeFilters =
            dispatchStatusFilter !== DISPATCH_COLUMN[16].name ||
            stateFilter !== DISPATCH_COLUMN[17].name ||
            billStatusFilter !== DISPATCH_COLUMN[15].name;
          refreshGroupByOptions(includeFilters);
        }
      }
    }
  }, [dispatchStatusFilter, stateFilter, billStatusFilter, getDispatchList, groupArray, groupBy, initialLoadComplete, refreshGroupByOptions, searchParams, setPageNumber]); // Only trigger on filter changes

  /**
   * Effect: Fetch group options when groupBy field changes
   */
  useEffect(() => {
    if (!consumeDispatchTrigger(triggers, 'groupBy', [groupBy])) return;
    if (initialLoadComplete && groupArray.includes(groupBy)) {
      refreshGroupByOptions(false);
    }
  }, [groupBy, groupArray, initialLoadComplete, refreshGroupByOptions]); // Only trigger on groupBy change

  /**
   * Effect: Handle search term cleared
   */
  useEffect(() => {
    if (!consumeDispatchTrigger(triggers, 'searchTerm', [searchTerm])) return;
    if (initialLoadComplete && !searchTerm && search === false) {
      // Only trigger if we had a search before (URL has search param)
      const urlSearch = searchParams.get('search');
      if (urlSearch) {
        setPageNumber(1);
        getDispatchList({ page_number: 1 });

        if (groupArray.includes(groupBy)) {
          refreshGroupByOptions(false);
        }
      }
    }
  }, [searchTerm, getDispatchList, groupArray, groupBy, initialLoadComplete, refreshGroupByOptions, search, searchParams, setPageNumber]); // Only trigger on searchTerm change

  /**
   * Effect: Apply groupBy when groupApplied flag changes
   */
  useEffect(() => {
    if (!consumeDispatchTrigger(triggers, 'groupApplied', [groupApplied])) return;
    if (initialLoadComplete && groupApplied) {
      getDispatchList({ page_number: 1 });
    }
  }, [groupApplied, getDispatchList, initialLoadComplete]); // Only trigger on groupApplied change

  /**
   * Effect: Handle reload flag
   */
  useEffect(() => {
    if (!consumeDispatchTrigger(triggers, 'reload', [reload])) return;
    if (reload) {
      getDispatchList({ page_number: 1 });
    }
  }, [reload, getDispatchList]); // Only trigger on reload flag change

  // =====================================================================
  // RETURN VALUE
  // =====================================================================

  return {
    // Data state
    dispatchData,
    isLoading,

    // Reload trigger
    reload,
    setReload,

    // Session state
    initialLoadComplete,

    // Main fetch function
    getDispatchList,

    // GroupBy values fetcher
    fetchGroupByValues,

    // Refresh helper
    refreshGroupByOptions,
  };
};
